/* eslint react-hooks/exhaustive-deps: "warn" */
import { type ReactNode, useEffect, useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InvestmentScreeningDesks.module.scss';

type Translate = (key: string) => string;

type Desk = {
  num: string;
  headline: ReactNode;
  tag: string;
  paragraphs: string[];
};

const getDesks = (t: Translate): Desk[] => [
  {
    num: '01',
    headline: (
      <>
        {t('Build the sourcing universe from')} <em>{t('filed accounts')}</em>
        {t(', not from announcements.')}
      </>
    ),
    tag: t('Private equity and growth'),
    paragraphs: [
      t('Proprietary origination starts with a universe nobody else has ranked.'),
      t(
        'Screen on turnover, margin, net assets and filing recency. The profitable, unbanked businesses surface before a process starts.',
      ),
      t('The ownership chain then tells you on day one whether there is a seller to talk to.'),
    ],
  },
  {
    num: '02',
    headline: (
      <>
        {t('Screen every market you operate in on')} <em>{t('the same fields')}</em>.
      </>
    ),
    tag: t('Corporate development'),
    paragraphs: [
      t('Adjacency mapping across twelve countries usually means twelve datasets with twelve schemas.'),
      t('One registry-first record covers 200+ countries on consistent fields.'),
      t('A screen built for Germany runs in Poland and Sweden with no new licence and no new mapping exercise.'),
    ],
  },
  {
    num: '03',
    headline: (
      <>
        {t('Build a buyer list that')} <em>{t('survives the pitch')}</em>.
      </>
    ),
    tag: t('Investment banking and advisory'),
    paragraphs: [
      t('A buyer list is only as good as the evidence under it.'),
      t('Deal history, group structure and filed financials sit in one record.'),
      t(
        'You can show who has bought in this sector, who controls them, and whether the balance sheet carries another deal.',
      ),
    ],
  },
];

export const InvestmentScreeningDesks = () => {
  const { t } = useInt();
  const desks = getDesks(t);
  const headRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const revealables = [headRef.current, ...rowRefs.current].filter((el): el is HTMLDivElement => Boolean(el));

    if (!revealables.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.revealed);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 },
    );

    revealables.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <Section background="#ffffff" className={styles.root}>
      <div ref={headRef} className={cn(styles.head, styles.reveal)}>
        <TitleBlock align="left" title={t('Three desks, one record')} />
      </div>

      <div className={styles.rows}>
        {desks.map((desk, i) => (
          <div
            key={desk.num}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className={cn(styles.row, styles.reveal)}
            style={{ transitionDelay: `${i * 100}ms` }}
          >
            <div className={styles.headlineCol}>
              <span className={styles.num}>{desk.num}</span>
              <div className={styles.big}>{desk.headline}</div>
            </div>

            <div className={styles.copyCol}>
              <span className={styles.tag}>{desk.tag}</span>
              {desk.paragraphs.map((p) => (
                <p key={p} className={styles.p}>
                  {p}
                </p>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};
